import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  JourneyMapDraft,
  JourneyMapDraftItem,
  JourneyMapDraftLink,
  JourneyMapDraftPathGroup,
  JourneyMapDraftPathGroupItem,
  JourneyMapSectionDelimiterDraft,
} from '../../types/journeyMap';
import {
  createDraftItem,
  createDraftLink,
  createDraftSectionDelimiter,
  createInitialDraft,
  makeTempId,
} from '../journeyMap/builderFactory';
import { getNextIncrementedName, getNextItemOrder, recalculateOrdersFromLayout } from '../journeyMap/builderLayout';
import { normalizeDraft, normalizeLinkDefaults } from '../journeyMap/builderNormailization';

type BuilderState = {
  drafts: JourneyMapDraft[];
  activeDraftId: string | null;
};

const initialDraft = createInitialDraft();

const initialState: BuilderState = {
  drafts: [initialDraft],
  activeDraftId: initialDraft.tempId,
};

const getActiveDraft = (state: BuilderState) =>
  state.drafts.find((d) => d.tempId === state.activeDraftId) || null;

const markDirty = (draft: JourneyMapDraft) => {
  draft.isDirty = true;
};

const removeLinkedReferencesForItem = (draft: JourneyMapDraft, itemTempId: string) => {
  draft.links = draft.links.flatMap((link) => {
    const touchesItem =
      link.from_item_temp_id === itemTempId || link.to_item_temp_id === itemTempId;

    if (!touchesItem) return [link];

    if (!link.id) return [];

    return [{ ...link, isDeleted: true, isDirty: true }];
  });

  draft.pathGroups.forEach((group) => {
    group.items = group.items.flatMap((groupItem) => {
      const touchesItem = groupItem.journey_map_item_temp_id === itemTempId;
      if (!touchesItem) return [groupItem];

      group.isDirty = true;

      if (!groupItem.id) return [];

      return [{ ...groupItem, isDeleted: true, isDirty: true }];
    });
  });
};

const journeyMapBuilderSlice = createSlice({
  name: 'journeyMapBuilder',
  initialState,
  reducers: {
    resetBuilderState(state) {
      const draft = createInitialDraft();
      state.drafts = [draft];
      state.activeDraftId = draft.tempId;
    },

    setActiveDraft(state, action: PayloadAction<JourneyMapDraft>) {
      const normalizedDraft = normalizeDraft(action.payload);
      const idx = state.drafts.findIndex((d) => d.tempId === normalizedDraft.tempId);
      if (idx >= 0) state.drafts[idx] = normalizedDraft;
      else state.drafts.push(normalizedDraft);
      state.activeDraftId = normalizedDraft.tempId;
    },

    setActiveDraftId(state, action: PayloadAction<string | null>) {
      state.activeDraftId = action.payload;
    },

    addJourneyMapDraft(state, action: PayloadAction<JourneyMapDraft | undefined>) {
      const draft = action.payload ? normalizeDraft(action.payload) : createInitialDraft();
      state.drafts.push(draft);
      state.activeDraftId = draft.tempId;
    },

    removeJourneyMapDraft(state, action: PayloadAction<string>) {
      state.drafts = state.drafts.filter((d) => d.tempId !== action.payload);
      if (state.activeDraftId === action.payload) {
        state.activeDraftId = state.drafts[0]?.tempId || null;
      }
      if (!state.drafts.length) {
        const draft = createInitialDraft();
        state.drafts = [draft];
        state.activeDraftId = draft.tempId;
      }
    },

    updateJourneyMapDraftField(
      state,
      action: PayloadAction<{
        field: keyof JourneyMapDraft;
        value: any;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      (draft as any)[action.payload.field] = action.payload.value;
      markDirty(draft);
    },

    selectJourneyMapDraftItem(state, action: PayloadAction<string | null>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.selectedItemTempId = action.payload;
      if (action.payload) {
        draft.selectedLinkTempId = null;
        draft.selectedPathGroupTempId = null;
      }
    },

    selectJourneyMapDraftLink(state, action: PayloadAction<string | null>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.selectedLinkTempId = action.payload;
      if (action.payload) {
        draft.selectedItemTempId = null;
        draft.selectedPathGroupTempId = null;
      }
    },

    selectJourneyMapDraftPathGroup(state, action: PayloadAction<string | null>) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      draft.selectedPathGroupTempId = action.payload;
      if (action.payload) {
        draft.selectedItemTempId = null;
        draft.selectedLinkTempId = null;
      }
    },

    addJourneyMapDraftItem(
      state,
      action: PayloadAction<Partial<JourneyMapDraftItem> | undefined>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const itemType = action.payload?.item_type || 'content';
      const baseName = itemType === 'section' ? 'New Section' : 'New Item';

      const item = createDraftItem({
        ...action.payload,
        item_type: itemType,
        name: action.payload?.name || getNextIncrementedName(draft, baseName),
        order: action.payload?.order ?? getNextItemOrder(draft),
      });

      draft.items.push(item);
      draft.selectedItemTempId = item.tempId;
      draft.selectedLinkTempId = null;
      draft.selectedPathGroupTempId = null;
      markDirty(draft);
    },

    addJourneyMapDraftItemAfter(
      state,
      action: PayloadAction<{
        sourceItemTempId: string;
        kind?: 'content' | 'section';
        linkType?: 'default' | 'alternate' | 'conditional' | 'test_out' | 'optional' | 'fallback';
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const source = draft.items.find((i) => i.tempId === action.payload.sourceItemTempId && !i.isDeleted);
      if (!source) return;

      const itemType = action.payload.kind || 'content';
      const baseName = itemType === 'section' ? 'New Section' : 'New Item';

      const item = createDraftItem({
        name: getNextIncrementedName(draft, baseName),
        item_type: itemType,
        order: getNextItemOrder(draft),
        position_x: (source.position_x ?? 240) + 320,
        position_y:
          action.payload.linkType === 'conditional' ||
          action.payload.linkType === 'test_out' ||
          action.payload.linkType === 'alternate'
            ? (source.position_y ?? 140) + 140
            : (source.position_y ?? 140),
      });

      draft.items.push(item);

      const link = createDraftLink({
        from_item_temp_id: source.tempId,
        to_item_temp_id: item.tempId,
        link_type: action.payload.linkType || 'default',
        label:
          action.payload.linkType === 'conditional'
            ? 'Conditional'
            : action.payload.linkType === 'test_out'
              ? 'Test Out'
              : action.payload.linkType === 'alternate'
                ? 'Alternate'
                : '',
      });

      draft.links.push(link);
      draft.selectedItemTempId = item.tempId;
      draft.selectedLinkTempId = null;
      draft.selectedPathGroupTempId = null;
      recalculateOrdersFromLayout(draft);
      markDirty(draft);
    },

    addJourneyMapDraftFirstItem(
      state,
      action: PayloadAction<{ kind?: 'content' | 'section' } | undefined>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      if (draft.items.filter((i) => !i.isDeleted).length > 0) return;

      const itemType = action.payload?.kind || 'content';
      const baseName = itemType === 'section' ? 'New Section' : 'New Item';

      const item = createDraftItem({
        name: getNextIncrementedName(draft, baseName),
        item_type: itemType,
        order: 1,
        position_x: 260,
        position_y: 140,
      });

      draft.items.push(item);
      draft.selectedItemTempId = item.tempId;
      draft.selectedLinkTempId = null;
      draft.selectedPathGroupTempId = null;
      markDirty(draft);
    },

    addJourneyMapDraftLinkBetweenItems(
      state,
      action: PayloadAction<{
        fromItemTempId: string;
        toItemTempId: string;
        linkType?: 'default' | 'alternate' | 'conditional' | 'test_out' | 'optional' | 'fallback';
        label?: string;
        sourceHandle?: string | null;
        targetHandle?: string | null;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      if (action.payload.fromItemTempId === action.payload.toItemTempId) return;

      const fromExists = draft.items.some(
        (item) => item.tempId === action.payload.fromItemTempId && !item.isDeleted,
      );
      const toExists = draft.items.some(
        (item) => item.tempId === action.payload.toItemTempId && !item.isDeleted,
      );

      if (!fromExists || !toExists) return;

      // Multiple links may connect the same pair as long as they differ by link_type
      // (e.g. a pass path and a fail path). Only an identical (from, to, link_type)
      // triple is treated as the same edge and updated in place.
      const nextLinkType = action.payload.linkType || 'default';
      const existingLink = draft.links.find(
        (link) =>
          !link.isDeleted &&
          link.from_item_temp_id === action.payload.fromItemTempId &&
          link.to_item_temp_id === action.payload.toItemTempId &&
          (link.link_type || 'default') === nextLinkType,
      );

      if (existingLink) {
        existingLink.source_handle = action.payload.sourceHandle ?? existingLink.source_handle ?? null;
        existingLink.target_handle = action.payload.targetHandle ?? existingLink.target_handle ?? null;
        existingLink.isDirty = true;
        draft.selectedLinkTempId = existingLink.tempId;
        draft.selectedItemTempId = null;
        draft.selectedPathGroupTempId = null;
        markDirty(draft);
        return;
      }

      const link = createDraftLink({
        from_item_temp_id: action.payload.fromItemTempId,
        to_item_temp_id: action.payload.toItemTempId,
        link_type: action.payload.linkType || 'default',
        label: action.payload.label || '',
        source_handle: action.payload.sourceHandle ?? null,
        target_handle: action.payload.targetHandle ?? null,
      });

      draft.links.push(link);
      draft.selectedLinkTempId = link.tempId;
      draft.selectedItemTempId = null;
      draft.selectedPathGroupTempId = null;
      markDirty(draft);
    },

    updateJourneyMapDraftItem(
      state,
      action: PayloadAction<{
        tempId: string;
        changes: Partial<JourneyMapDraftItem>;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const item = draft.items.find((i) => i.tempId === action.payload.tempId);
      if (!item) return;

      Object.assign(item, action.payload.changes);
      item.isDirty = true;

      const changes = action.payload.changes || {};
      const affectsLayout =
        changes.order !== undefined ||
        changes.position_x !== undefined ||
        changes.position_y !== undefined;

      if (affectsLayout) {
        recalculateOrdersFromLayout(draft);
      }

      markDirty(draft);
    },

    removeJourneyMapDraftItem(state, action: PayloadAction<string>) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const itemIndex = draft.items.findIndex((i) => i.tempId === action.payload);
      if (itemIndex === -1) return;

      const item = draft.items[itemIndex];

      removeLinkedReferencesForItem(draft, action.payload);

      if (!item.id) {
        draft.items.splice(itemIndex, 1);
      } else {
        item.isDeleted = true;
        item.isDirty = true;
      }

      if (draft.selectedItemTempId === action.payload) {
        draft.selectedItemTempId = null;
      }

      recalculateOrdersFromLayout(draft);
      markDirty(draft);
    },

    updateJourneyMapDraftItemPosition(
      state,
      action: PayloadAction<{
        tempId: string;
        position_x: number;
        position_y: number;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const item = draft.items.find((i) => i.tempId === action.payload.tempId);
      if (!item) return;

      item.position_x = action.payload.position_x;
      item.position_y = action.payload.position_y;
      item.isDirty = true;

      recalculateOrdersFromLayout(draft);
      markDirty(draft);
    },

    addJourneyMapDraftLink(
      state,
      action: PayloadAction<Partial<JourneyMapDraftLink> | undefined>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const link = createDraftLink(action.payload);
      draft.links.push(link);
      draft.selectedLinkTempId = link.tempId;
      draft.selectedItemTempId = null;
      draft.selectedPathGroupTempId = null;
      markDirty(draft);
    },

    updateJourneyMapDraftLink(
      state,
      action: PayloadAction<{
        tempId: string;
        changes: Partial<JourneyMapDraftLink>;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const link = draft.links.find((l) => l.tempId === action.payload.tempId);
      if (!link) return;

      const nextChanges = { ...action.payload.changes };

      if (nextChanges.link_type) {
        const normalized = normalizeLinkDefaults(
          nextChanges.link_type,
          nextChanges.condition_type || link.condition_type,
          nextChanges.condition_config || link.condition_config,
        );

        if (nextChanges.condition_type === undefined) {
          nextChanges.condition_type = normalized.condition_type;
        }

        nextChanges.condition_config = {
          ...normalized.condition_config,
          ...(nextChanges.condition_config || {}),
        };
      }

      if (nextChanges.condition_type && !nextChanges.link_type) {
        const nextConfig = {
          ...(link.condition_config || {}),
          ...(nextChanges.condition_config || {}),
        };

        if (nextChanges.condition_type !== 'status_equals') {
          delete nextConfig.status;
        }
        if (
          nextChanges.condition_type !== 'score_gte' &&
          nextChanges.condition_type !== 'score_gt' &&
          nextChanges.condition_type !== 'score_lte' &&
          nextChanges.condition_type !== 'score_lt'
        ) {
          delete nextConfig.score;
        }
        if (nextChanges.condition_type !== 'rule') {
          delete nextConfig.rule_expression;
        }

        nextChanges.condition_config = nextConfig;
      }

      Object.assign(link, nextChanges);
      link.isDirty = true;
      markDirty(draft);
    },

    removeJourneyMapDraftLink(state, action: PayloadAction<string>) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const linkIndex = draft.links.findIndex((l) => l.tempId === action.payload);
      if (linkIndex === -1) return;

      const link = draft.links[linkIndex];

      if (!link.id) {
        draft.links.splice(linkIndex, 1);
      } else {
        link.isDeleted = true;
        link.isDirty = true;
      }

      if (draft.selectedLinkTempId === action.payload) {
        draft.selectedLinkTempId = null;
      }

      markDirty(draft);
    },

    addJourneyMapDraftPathGroup(
      state,
      action: PayloadAction<Partial<JourneyMapDraftPathGroup> | undefined>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const existingCount = draft.pathGroups.filter((g) => !g.isDeleted).length + 1;

      const group: JourneyMapDraftPathGroup = {
        tempId: makeTempId('journey-map-path-group'),
        id: action.payload?.id,
        journey_map: action.payload?.journey_map,
        name: action.payload?.name || `New Path Group ${existingCount}`,
        description: action.payload?.description || '',
        group_type: action.payload?.group_type || 'all_of',
        min_required: action.payload?.min_required ?? 1,
        is_required: action.payload?.is_required ?? false,
        order: action.payload?.order ?? draft.pathGroups.length + 1,
        metadata: action.payload?.metadata || {},
        items: action.payload?.items || [],
        isNew: true,
        isDirty: true,
        isDeleted: false,
      };

      draft.pathGroups.push(group);
      draft.selectedPathGroupTempId = group.tempId;
      draft.selectedItemTempId = null;
      draft.selectedLinkTempId = null;
      markDirty(draft);
    },

    updateJourneyMapDraftPathGroup(
      state,
      action: PayloadAction<{
        tempId: string;
        changes: Partial<JourneyMapDraftPathGroup>;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      const group = draft.pathGroups.find((g) => g.tempId === action.payload.tempId);
      if (!group) return;
      Object.assign(group, action.payload.changes);
      group.isDirty = true;
      markDirty(draft);
    },

    removeJourneyMapDraftPathGroup(state, action: PayloadAction<string>) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const groupIndex = draft.pathGroups.findIndex((g) => g.tempId === action.payload);
      if (groupIndex === -1) return;

      const group = draft.pathGroups[groupIndex];

      if (!group.id) {
        draft.pathGroups.splice(groupIndex, 1);
      } else {
        group.isDeleted = true;
        group.isDirty = true;
      }

      if (draft.selectedPathGroupTempId === action.payload) {
        draft.selectedPathGroupTempId = null;
      }

      markDirty(draft);
    },

    addJourneyMapDraftPathGroupItem(
      state,
      action: PayloadAction<{
        pathGroupTempId: string;
        journeyMapItemTempId: string;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const group = draft.pathGroups.find((g) => g.tempId === action.payload.pathGroupTempId);
      if (!group) return;

      const alreadyExists = group.items.some(
        (i) =>
          !i.isDeleted &&
          i.journey_map_item_temp_id === action.payload.journeyMapItemTempId,
      );
      if (alreadyExists) return;

      const item: JourneyMapDraftPathGroupItem = {
        tempId: makeTempId('journey-map-path-group-item'),
        path_group: group.id,
        path_group_temp_id: group.tempId,
        journey_map_item: undefined,
        journey_map_item_temp_id: action.payload.journeyMapItemTempId,
        order: group.items.filter((i) => !i.isDeleted).length + 1,
        isNew: true,
        isDirty: true,
        isDeleted: false,
      };

      group.items.push(item);
      group.isDirty = true;
      markDirty(draft);
    },

    removeJourneyMapDraftPathGroupItem(
      state,
      action: PayloadAction<{
        pathGroupTempId: string;
        itemTempId: string;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      const group = draft.pathGroups.find((g) => g.tempId === action.payload.pathGroupTempId);
      if (!group) return;

      const itemIndex = group.items.findIndex((i) => i.tempId === action.payload.itemTempId);
      if (itemIndex === -1) return;

      const item = group.items[itemIndex];

      if (!item.id) {
        group.items.splice(itemIndex, 1);
      } else {
        item.isDeleted = true;
        item.isDirty = true;
      }

      group.isDirty = true;
      markDirty(draft);
    },

    addJourneyMapDraftSectionDelimiter(
      state,
      action: PayloadAction<Partial<JourneyMapSectionDelimiterDraft> | undefined>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft) return;

      draft.section_delimiters = draft.section_delimiters || [];
      const delimiter = createDraftSectionDelimiter(action.payload);
      draft.section_delimiters.push(delimiter);
      markDirty(draft);
    },

    updateJourneyMapDraftSectionDelimiter(
      state,
      action: PayloadAction<{
        tempId: string;
        changes: Partial<JourneyMapSectionDelimiterDraft>;
      }>,
    ) {
      const draft = getActiveDraft(state);
      if (!draft || !draft.section_delimiters) return;

      const delimiter = draft.section_delimiters.find((d) => d.tempId === action.payload.tempId);
      if (!delimiter) return;

      Object.assign(delimiter, action.payload.changes);
      delimiter.isDirty = true;
      markDirty(draft);
    },

    removeJourneyMapDraftSectionDelimiter(state, action: PayloadAction<string>) {
      const draft = getActiveDraft(state);
      if (!draft || !draft.section_delimiters) return;

      const delimiterIndex = draft.section_delimiters.findIndex((d) => d.tempId === action.payload);
      if (delimiterIndex === -1) return;

      const delimiter = draft.section_delimiters[delimiterIndex];

      if (!delimiter.id) {
        draft.section_delimiters.splice(delimiterIndex, 1);
      } else {
        delimiter.isDeleted = true;
        delimiter.isDirty = true;
      }

      markDirty(draft);
    },

    recalculateDraftOrderFromLayout(state) {
      const draft = getActiveDraft(state);
      if (!draft) return;
      recalculateOrdersFromLayout(draft);
      markDirty(draft);
    },
  },
});

export const {
  resetBuilderState,
  setActiveDraft,
  setActiveDraftId,
  addJourneyMapDraft,
  removeJourneyMapDraft,
  updateJourneyMapDraftField,
  selectJourneyMapDraftItem,
  selectJourneyMapDraftLink,
  selectJourneyMapDraftPathGroup,
  addJourneyMapDraftItem,
  addJourneyMapDraftFirstItem,
  addJourneyMapDraftItemAfter,
  addJourneyMapDraftLinkBetweenItems,
  updateJourneyMapDraftItem,
  removeJourneyMapDraftItem,
  updateJourneyMapDraftItemPosition,
  addJourneyMapDraftLink,
  updateJourneyMapDraftLink,
  removeJourneyMapDraftLink,
  addJourneyMapDraftPathGroup,
  updateJourneyMapDraftPathGroup,
  removeJourneyMapDraftPathGroup,
  addJourneyMapDraftPathGroupItem,
  removeJourneyMapDraftPathGroupItem,
  addJourneyMapDraftSectionDelimiter,
  updateJourneyMapDraftSectionDelimiter,
  removeJourneyMapDraftSectionDelimiter,
  recalculateDraftOrderFromLayout,
} = journeyMapBuilderSlice.actions;

export default journeyMapBuilderSlice.reducer;